import type { Directive, VNode } from 'vue'
import Tooltip from 'primevue/tooltip'

/**
 * v-overflow-tooltip="'text'"
 *
 * Shows a PrimeVue bottom tooltip only when the element's text content is
 * actually truncated. Avoids noisy tooltips on values that fit without clipping.
 *
 * The element must have `overflow: hidden` / `truncate` applied for the
 * truncation check to be meaningful.
 */

interface OverflowTooltipEl extends HTMLElement {
  _overflowTooltipCheck?: (e: MouseEvent) => void
  _overflowTooltipObserver?: ResizeObserver
  $_ptooltipDisabled?: boolean
}

function makeBinding(value: string, vnode: VNode) {
  return {
    value: { value, disabled: true, showDelay: 150, hideDelay: 125 },
    modifiers: { bottom: true },
    // PrimeVue's Tooltip.js accesses options.instance.$primevue directly —
    // pass the component's public proxy so it can resolve the global PrimeVue config.
    instance: (vnode as any).ctx?.proxy ?? (vnode as any).ctx,
  } as any
}

/**
 * Accurately detects text truncation by comparing the content's natural pixel
 * width (via Range) to the element's rendered width (via getBoundingClientRect).
 *
 * Why not scrollWidth/clientWidth: both return integers, so sub-pixel font
 * rendering at non-100% zoom can make scrollWidth appear larger than clientWidth
 * even when no ellipsis is shown — producing false positives.
 * Range + getBoundingClientRect work at full floating-point precision.
 */
export function isTruncated(el: HTMLElement): boolean {
  if (!el.childNodes.length) return false
  const range = document.createRange()
  range.setStart(el, 0)
  range.setEnd(el, el.childNodes.length)
  // Allow 1px tolerance for sub-pixel rendering differences across browsers
  return range.getBoundingClientRect().width > el.getBoundingClientRect().width + 1
}

export const vOverflowTooltip: Directive<OverflowTooltipEl, string | undefined> = {
  // BaseDirective.created initializes el.$pd — must run before beforeMount/mounted
  created(el, binding, vnode, prevVnode) {
    if (!binding.value) return
    Tooltip.created!(el, makeBinding(binding.value, vnode), vnode, prevVnode)
  },

  beforeMount(el, binding, vnode, prevVnode) {
    if (!binding.value) return
    Tooltip.beforeMount!(el, makeBinding(binding.value, vnode), vnode, prevVnode)
  },

  mounted(el, binding, vnode, prevVnode) {
    if (!binding.value) return

    Tooltip.mounted!(el, makeBinding(binding.value, vnode), vnode, prevVnode)

    // Sync disabled flag eagerly so the very first hover is accurate
    el.$_ptooltipDisabled = !isTruncated(el)

    // Keep the flag in sync whenever the element is resized (e.g. column resize,
    // window resize, font load) so it never goes stale between hovers.
    const observer = new ResizeObserver(() => {
      el.$_ptooltipDisabled = !isTruncated(el)
    })
    observer.observe(el)
    el._overflowTooltipObserver = observer

    // Re-check on mouseenter — runs before PrimeVue's own listener (registered
    // above in Tooltip.mounted), so $ptooltipDisabled is set before PrimeVue's
    // showDelay timeout fires.
    const check = () => {
      el.$_ptooltipDisabled = !isTruncated(el)
    }
    el.addEventListener('mouseenter', check)
    el._overflowTooltipCheck = check
  },

  updated(el, binding, vnode, prevVnode) {
    if (!binding.value) return
    Tooltip.updated!(el, makeBinding(binding.value, vnode), vnode, prevVnode)
    el.$_ptooltipDisabled = !isTruncated(el)
  },

  unmounted(el, binding, vnode, prevVnode) {
    el._overflowTooltipObserver?.disconnect()
    if (el._overflowTooltipCheck) {
      el.removeEventListener('mouseenter', el._overflowTooltipCheck)
    }
    if (binding.value) {
      Tooltip.unmounted!(el, binding as any, vnode, prevVnode)
    }
  },
}
